import { Editor, EditorPlugin, ExtractContentEvent, PluginEvent, PluginEventType } from "RoosterReact/rooster-react-amd";
import { Constants } from "Mention/Scripts/TFS.Mention";
import { richTextFilterContentAndPreRender } from "WorkItemTracking/Scripts/Utils/RichTextPreRenderUtility";
import { HtmlFieldsMentionsUsageTelemetryHelper } from "WorkItemTracking/Scripts/Components/HtmlFieldsMentionsTelemetry";

const MENTION_BEGIN_TAG = "MentionBegin";
const MENTION_END_TAG = "MentionEnd";
const EXTRACT_HTML_REGEX = new RegExp(`<!--${MENTION_BEGIN_TAG}-->.+?<!--(<a[^>]+>.+?<\/a>)--><!--${MENTION_END_TAG}-->`, "gi");

/**
 * This plugin is for rendering HTML mentions in HTML fields. It renders mentions on blur, and unrenders them when you focus into edit mode.
 */
export class HtmlEditorMentionPlugin implements EditorPlugin {
    protected editor: Editor;
    private _disposer: () => void;
    private _latestRenderedId: number;
    private _hasFocus: boolean;

    public getName(): string {
        return "HtmlEditorMentionPlugin";
    }

    public initialize(editor: Editor): void {
        this.editor = editor;
        this._hasFocus = false;
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.EditorReady || event.eventType === PluginEventType.ContentChanged) {
            this._renderMentions();
        } else if (event.eventType === PluginEventType.ExtractContent) {
            const extractContentEvent = event as ExtractContentEvent;
            extractContentEvent.content = this._getRawMentions(extractContentEvent.content);
        }
    }

    public dispose(): void {
        if (this._disposer) {
            this._disposer();
            this._disposer = null;
        }
        this.editor = null;
    }

    public onBlur = () => {
        this._hasFocus = false;
        this._renderMentions();
    }

    public onFocus = () => {
        this._hasFocus = true;
        this._revertRenderedMentions();
    }

    private _renderMentions(): void {
        const latestContent = this._getOriginalContent();
        const currentRenderedId = (this._latestRenderedId = Date.now());

        if (latestContent && latestContent.indexOf(Constants.HTML_MENTION_ATTR_NAME) !== -1) {
            // _lastRenderedId is to prevent race conditions from happening. It ensures that the lastest content is always the one that gets rendered. Because
            // the richTextFilterContentAndPreRender function is asynchronous, on a slow machine or if done fast enough, its possible that old content gets rendered over new content
            // which would result in data loss.
            this._runRichTextFilterContentAndPreRender(latestContent, false).then(renderedContent => {
                if (!this.editor || currentRenderedId !== this._latestRenderedId || this._hasFocus) {
                    return;
                }

                // Here we set triggerContentChangedEvent to false because we don't want to push the rendered mention changes to the other plugins.
                this.editor.setContent(renderedContent, false /* triggerContentChangedEvent */);
            }).catch(e => HtmlFieldsMentionsUsageTelemetryHelper.publishMentionRenderFail(e));
        }
    }

    private _revertRenderedMentions() { 
        this.editor.setContent(this._getOriginalContent(), false /* triggerContentChangedEvent */); 
    }


    private _getRawMentions(content: string): string {
        return content.replace(EXTRACT_HTML_REGEX, "$1");
    }

    // Returns exact contents of the editor (including temporary plugin data) without rendering Mentions
    private _getOriginalContent(): string {
        return this._getRawMentions(this.editor.getContent(false));
    }

    /**
     * Mention rendering function used in the HtmlEditorMentionPlugin
     * @param content Text content to render
     * @param enableContactCard If true, renders contact card on identity mentions
     * @param renderOptions Render options for work item mentions
     */
    private _runRichTextFilterContentAndPreRender(
        text: string,
        enableContactCard: boolean
    ): Promise<string> {
        const content = document.createElement("div");
        content.innerHTML = text;
        
        // To allow for easy unrendering of mentions, we create the rendered mention in this format:
        // <!--MentionBegin-->
        // <rendered mention />
        // <!--unrenderedMention-->
        // <!--MentionEnd-->
        // This way we can easily find the mentions in the rendered text and extract the unrendered version of it for un-rendering.
        const mentionAnchors: Element[] = [].slice.call(content.querySelectorAll(`a[${Constants.HTML_MENTION_ATTR_NAME}]`));
        mentionAnchors.forEach((a) => {
            a.parentElement.insertBefore(document.createComment(MENTION_BEGIN_TAG), a);
            a.parentElement.insertBefore(document.createComment(MENTION_END_TAG), a.nextSibling);
            a.parentElement.insertBefore(document.createComment(a.outerHTML), a.nextSibling); // a.nextSibling is the END comment
        });

        return richTextFilterContentAndPreRender(content, enableContactCard, undefined, false /* shouldSanitizeExternalLinks */);
    }
}
